import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import AdminLayout from "../components/AdminLayout";
import { Loader2, Download, Calendar, Truck, User, MapPin, Clock, TrendingUp, FileText, Filter, X } from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { fetchVehicles } from "../actions/VehicleActions";
import { fetchUsers } from "../actions/UserActions";
import { getAllBookings } from "../services/bookingService";

const PAGE_SIZE = 10;

function parseDateFlexible(value) {
  if (!value && value !== 0) return null;
  if (value instanceof Date && !isNaN(value)) return value;
  if (!isNaN(value) && typeof value === "number") return new Date(value);
  const iso = new Date(value);
  if (!isNaN(iso.getTime())) return iso;
  try {
    const dt = Date.parse(value.toString().trim());
    if (!isNaN(dt)) return new Date(dt);
  } catch (e) {}
  return null;
}

// Stat Card Component
function StatCard({ title, value, icon: Icon, color, subtitle }) {
  const colorClasses = {
    blue: { bg: "bg-blue-50", iconBg: "bg-blue-100", text: "text-blue-600" },
    green: { bg: "bg-emerald-50", iconBg: "bg-emerald-100", text: "text-emerald-600" },
    orange: { bg: "bg-orange-50", iconBg: "bg-orange-100", text: "text-orange-600" },
    purple: { bg: "bg-purple-50", iconBg: "bg-purple-100", text: "text-purple-600" },
  };
  
  const colors = colorClasses[color] || colorClasses.blue;
  
  return (
    <div className={`${colors.bg} border border-gray-100 rounded-lg p-3 transition-all hover:shadow-sm`}>
      <div className="flex items-center justify-between mb-1.5">
        <div className={`${colors.iconBg} p-1.5 rounded-lg`}>
          <Icon size={14} className={colors.text} />
        </div>
      </div>
      <p className="text-xl font-semibold text-gray-900">{value}</p>
      <p className="text-xs text-gray-600 mt-0.5">{title}</p>
      {subtitle && <p className="text-[10px] text-gray-400 mt-0.5">{subtitle}</p>}
    </div>
  );
}

// Chart Card Component
function ChartCard({ title, data, dataKey, color, valueLabel }) {
  const colorMap = {
    blue: "#3B82F6",
    green: "#10B981",
    orange: "#F59E0B",
    purple: "#8B5CF6",
  };
  
  return (
    <div className="bg-white border border-gray-100 rounded-lg p-4">
      <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
        <TrendingUp size={14} className="text-gray-400" />
        {title}
      </h3>
      <ResponsiveContainer width="100%" height={200}>
        <BarChart data={data} margin={{ top: 5, right: 5, left: 5, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
          <XAxis 
            dataKey={dataKey} 
            tick={{ fontSize: 10, fill: "#6B7280" }}
            tickLine={false}
          />
          <YAxis tick={{ fontSize: 10, fill: "#6B7280" }} tickLine={false} />
          <Tooltip 
            contentStyle={{ 
              backgroundColor: "white", 
              border: "1px solid #E5E7EB",
              borderRadius: "8px",
              fontSize: "12px"
            }}
          />
          <Bar 
            dataKey="hours" 
            fill={colorMap[color]} 
            radius={[4, 4, 0, 0]}
            barSize={30}
          />
        </BarChart>
      </ResponsiveContainer>
      <p className="text-xs text-gray-400 text-center mt-2">
        {valueLabel}
      </p>
    </div>
  );
}

// Booking Table Row Component
function BookingRow({ booking, index }) {
  return (
    <tr className={index % 2 === 0 ? "bg-white" : "bg-gray-50"}>
      <td className="px-3 py-2 text-sm font-mono text-gray-900">
        #{booking.bId?.slice(-6) || '-'}
      </td>
      <td className="px-3 py-2 text-sm text-gray-600">{booking.vehicle_id || '-'}</td>
      <td className="px-3 py-2 text-sm text-gray-600">{booking.driver_service_no || '-'}</td>
      <td className="px-3 py-2 text-sm text-gray-600">
        {booking.booking_start_time ? new Date(booking.booking_start_time).toLocaleString() : '-'}
      </td>
      <td className="px-3 py-2 text-sm text-gray-600">
        {booking.booking_end_time ? new Date(booking.booking_end_time).toLocaleString() : '-'}
      </td>
      <td className="px-3 py-2 text-sm font-medium text-gray-900">
        {(Number(booking.total_distance_km) || 0).toFixed(2)} km
      </td>
      <td className="px-3 py-2">
        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
          booking.status === "Active" 
            ? "bg-emerald-50 text-emerald-700" 
            : "bg-gray-100 text-gray-600"
        }`}>
          {booking.status || '-'}
        </span>
      </td>
    </tr>
  );
}

export default function AdminReports() {
  const [filterVehicle, setFilterVehicle] = useState("");
  const [filterDriver, setFilterDriver] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [page, setPage] = useState(1);
  const [exportScope, setExportScope] = useState("page");
  const [sidebarOpen, setSidebarOpen] = useState(true);

  const dispatch = useDispatch();
  const { authKey } = useSelector((state) => state.auth);
  const { items: vehicleItems, loading: vehiclesLoading, error: vehiclesError } = useSelector((state) => state.vehicle);
  const { items: userItems, loading: usersLoading, error: usersError } = useSelector((state) => state.user);
  const hasFetchedRef = React.useRef(false);
  const [reportBookings, setReportBookings] = useState([]);
  const [reportsLoading, setReportsLoading] = useState(true);
  const [reportsError, setReportsError] = useState("");

  const loading = vehiclesLoading || usersLoading || reportsLoading;
  const error = vehiclesError || usersError || reportsError || "";

  useEffect(() => {
    if (!authKey) return;
    if (!hasFetchedRef.current) {
      hasFetchedRef.current = true;
      dispatch(fetchVehicles());
      dispatch(fetchUsers());
      (async () => {
        try {
          setReportsLoading(true);
          const data = await getAllBookings();
          setReportBookings(Array.isArray(data) ? data : []);
          setReportsError("");
        } catch (err) {
          setReportsError(err?.message || "Failed to fetch bookings.");
        } finally {
          setReportsLoading(false);
        }
      })();
    }
  }, [authKey, dispatch]);

  const vehicles = useMemo(
    () =>
      (vehicleItems || []).map((v) => ({
        vehicle_id: v.vehicleId ?? v.vehicle_id ?? v.vehicleId,
        vehicle_type: v.vehicleType ?? v.vehicle_type ?? v.vehicleType,
        weight_category: v.weightCat ?? v.weight_category ?? v.weightCat,
        driver_service_no: v.driverSno ?? v.driver_service_no ?? v.driverSno,
        status: v.status ?? v.status,
        latitude: v.latitude ?? v.current_latitude ?? v.latitude,
        longitude: v.longitude ?? v.current_longitude ?? v.longitude,
      })),
    [vehicleItems]
  );

  const users = useMemo(
    () =>
      (userItems || []).map((u) => ({
        service_number: u.ServiceNo ?? u.service_number ?? u.ServiceNo,
        name: u.Name ?? u.name ?? u.Name,
        mobile: u.MobileNo ?? u.mobile_number ?? u.MobileNo,
        role: u.role ?? u.role,
        status: u.status ?? u.status,
      })),
    [userItems]
  );

  const bookings = useMemo(
    () =>
      (reportBookings || []).map((b) => {
        const bs = b.bsTime ?? b.booking_start_time ?? b.bookingStartTime ?? b.bsTime ?? "";
        const be = b.beTime ?? b.booking_end_time ?? b.bookingEndTime ?? b.beTime ?? "";
        const total = b.totalDestence ?? b.total_destence ?? b.total_distance_km ?? b.totalDistanceKm ?? 0;

        return {
          bId: b.bId ?? b.booking_id ?? b.bId,
          vehicle_id: b.vId ?? b.vehicleId ?? b.vehicle_id ?? b.vId,
          driver_service_no: b.dSNo ?? b.driverSno ?? b.driver_service_no ?? b.dSNo,
          start_latitude: b.slatitude ?? b.start_latitude ?? b.slat ?? "",
          start_longitude: b.slongitude ?? b.start_longitude ?? b.slon ?? "",
          end_latitude: b.elatitude ?? b.end_latitude ?? b.elat ?? "",
          end_longitude: b.elongitude ?? b.end_longitude ?? b.elon ?? "",
          bookingDT: b.bookingDT ?? b.bookingDT ?? b.created_date ?? "",
          booking_start_time: parseDateFlexible(bs),
          booking_end_time: parseDateFlexible(be),
          total_distance_km: parseFloat(Number(total || 0) || 0),
          status: (b.status === "A" || b.status === "Active") ? "Active" : (b.status === "I" || b.status === "Inactive" ? "Inactive" : (b.status ?? "")),
          raw: b,
        };
      }),
    [reportBookings]
  );

  useEffect(() => {
    setPage(1);
  }, [filterVehicle, filterDriver, fromDate, toDate]);

  const filteredBookings = useMemo(() => {
    const from = fromDate ? new Date(fromDate + "T00:00:00") : null;
    const to = toDate ? new Date(toDate + "T23:59:59.999") : null;
    const vehicleFilter = String(filterVehicle || "").trim().toUpperCase();
    const driverFilter = String(filterDriver || "").trim().toUpperCase();

    return bookings.filter((b) => {
      if (!b) return false;
      const matchVehicle = vehicleFilter
        ? String(b.vehicle_id || "").toUpperCase().includes(vehicleFilter)
        : true;
      const matchDriver = driverFilter
        ? String(b.driver_service_no || "").toUpperCase().includes(driverFilter)
        : true;

      const startTime = b.booking_start_time || parseDateFlexible(b.bookingDT);
      const afterFrom = from ? (startTime ? startTime >= from : false) : true;
      const beforeTo = to ? (startTime ? startTime <= to : false) : true;

      return matchVehicle && matchDriver && afterFrom && beforeTo;
    });
  }, [bookings, filterVehicle, filterDriver, fromDate, toDate]);

  const pageCount = Math.max(1, Math.ceil(filteredBookings.length / PAGE_SIZE));
  const currentPageBookings = filteredBookings.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  // Charts and totals
  const vehicleHours = useMemo(() => {
    const map = {};
    filteredBookings.forEach((b) => {
      if (!b.booking_start_time || !b.booking_end_time) return;
      const minutes = (b.booking_end_time - b.booking_start_time) / 60000;
      if (!map[b.vehicle_id]) map[b.vehicle_id] = 0;
      map[b.vehicle_id] += minutes;
    });
    return Object.keys(map).map((vid) => ({ vehicle_id: vid, hours: parseFloat((map[vid] / 60).toFixed(2)) })).slice(0, 10);
  }, [filteredBookings]);

  const driverHours = useMemo(() => {
    const map = {};
    filteredBookings.forEach((b) => {
      if (!b.booking_start_time || !b.booking_end_time) return;
      const minutes = (b.booking_end_time - b.booking_start_time) / 60000;
      if (!map[b.driver_service_no]) map[b.driver_service_no] = 0;
      map[b.driver_service_no] += minutes;
    });
    return Object.keys(map).map((d) => ({ driver: d, hours: parseFloat((map[d] / 60).toFixed(2)) })).slice(0, 10);
  }, [filteredBookings]);

  const vehicleDistance = useMemo(() => {
    const map = {};
    filteredBookings.forEach((b) => {
      const dist = Number(b.total_distance_km) || 0;
      if (!map[b.vehicle_id]) map[b.vehicle_id] = 0;
      map[b.vehicle_id] += dist;
    });
    return Object.keys(map).map((vid) => ({ vehicle_id: vid, distance: parseFloat(map[vid].toFixed(2)) })).slice(0, 10);
  }, [filteredBookings]);

  const totals = useMemo(() => {
    let totalDistance = 0;
    let totalMinutes = 0;
    filteredBookings.forEach((b) => {
      totalDistance += Number(b.total_distance_km) || 0;
      if (b.booking_start_time && b.booking_end_time) {
        totalMinutes += (b.booking_end_time - b.booking_start_time) / 60000;
      }
    });
    return { totalDistance: parseFloat(totalDistance.toFixed(2)), totalHours: parseFloat((totalMinutes / 60).toFixed(2)) };
  }, [filteredBookings]);

  const handleClearFilters = () => {
    setFilterVehicle("");
    setFilterDriver("");
    setFromDate("");
    setToDate("");
  };

  const downloadPDF = (scope = exportScope) => {
    try {
      const doc = new jsPDF({ unit: "pt", format: "a4" });
      const now = new Date();
      const title = `Bookings Report - ${scope === "page" ? "Page " + page : "All Filtered"}`;
      doc.setFontSize(16);
      doc.text(title, 40, 48);

      doc.setFontSize(10);
      const filterInfo = `Generated: ${now.toLocaleString()}  •  Vehicle: ${filterVehicle || "All"}  •  Driver: ${filterDriver || "All"}  •  From: ${fromDate || "-"} To: ${toDate || "-"}`;
      doc.text(filterInfo, 40, 66);

      const summaryTop = 86;
      const cardX = 40;
      const cardY = summaryTop;
      const cardW = 520;
      const cardH = 56;

      doc.setFillColor(245, 245, 245);
      doc.rect(cardX, cardY, cardW, cardH, "F");
      doc.setFontSize(11);
      doc.text(`Records: ${scope === "page" ? currentPageBookings.length : filteredBookings.length}`, cardX + 8, cardY + 18);
      doc.text(`Total Distance (KM): ${totals.totalDistance.toFixed(2)}`, cardX + 8, cardY + 36);
      doc.text(`Total Hours: ${totals.totalHours.toFixed(2)}`, cardX + 220, cardY + 36);

      const rowsSource = scope === "page" ? currentPageBookings : filteredBookings;
      const head = [["Booking ID", "Vehicle", "Driver", "Start Time", "End Time", "Distance (KM)", "Status"]];
      const body = rowsSource.map((r) => [
        r.bId ?? "-",
        r.vehicle_id ?? "-",
        r.driver_service_no ?? "-",
        r.booking_start_time ? new Date(r.booking_start_time).toLocaleString() : "-",
        r.booking_end_time ? new Date(r.booking_end_time).toLocaleString() : "-",
        (Number(r.total_distance_km) || 0).toFixed(2),
        r.status ?? "-",
      ]);

      autoTable(doc, {
        head,
        body,
        startY: cardY + cardH + 16,
        theme: "striped",
        headStyles: { fillColor: [17, 24, 39], textColor: 255, halign: 'center' },
        styles: { fontSize: 10, cellPadding: 6 },
        columnStyles: { 0: { cellWidth: 60 }, 1: { cellWidth: 60 }, 2: { cellWidth: 60 } },
        didDrawPage: (data) => {
          const pageNumber = doc.internal.getNumberOfPages();
          doc.setFontSize(9);
          doc.text(`Page ${pageNumber}`, doc.internal.pageSize.getWidth() - 60, doc.internal.pageSize.getHeight() - 30);
        },
      });

      const fileName = `BookingsReport_${scope === "page" ? "page_" + page : "all"}.pdf`;
      doc.save(fileName);
    } catch (err) {
      console.error("PDF export error:", err);
      alert("PDF export failed. Check console for details.");
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="animate-spin w-8 h-8 text-gray-400" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center p-6">
        <div className="bg-white border border-gray-100 rounded-lg p-6 text-center shadow-sm">
          <p className="text-red-600 mb-2 text-sm">{error}</p>
          <p className="text-xs text-gray-500">Please check your network and API key.</p>
        </div>
      </div>
    );
  }

  return (
    <AdminLayout>
      {/* Header Section */}
      <div className="mb-4">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
          <div>
            <h1 className="text-lg sm:text-xl font-semibold text-gray-900 flex items-center gap-2">
              <FileText size={20} />
              Reports & Analytics
            </h1>
            <p className="text-xs text-gray-500 mt-0.5">
              Comprehensive booking reports and performance metrics
            </p>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-3 gap-2 mb-4">
        <StatCard 
          title="Total Distance" 
          value={`${totals.totalDistance.toFixed(2)} km`} 
          icon={MapPin} 
          color="blue"
        />
        <StatCard 
          title="Working Hours" 
          value={`${totals.totalHours.toFixed(2)} h`} 
          icon={Clock} 
          color="green"
        />
        <StatCard 
          title="Total Bookings" 
          value={filteredBookings.length} 
          icon={FileText} 
          color="purple"
        />
      </div>

      {/* Filters Section */}
      <div className="bg-white border border-gray-100 rounded-lg p-4 mb-4">
        <div className="flex items-center gap-2 mb-3">
          <Filter size={14} className="text-gray-400" />
          <h2 className="text-sm font-semibold text-gray-900">Filters</h2>
        </div>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <div className="relative">
            <Truck size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={filterVehicle}
              onChange={(e) => setFilterVehicle(e.target.value)}
              placeholder="Vehicle ID"
              className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-gray-400 focus:ring-1 focus:ring-gray-400 transition"
            />
          </div>
          
          <div className="relative">
            <User size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={filterDriver}
              onChange={(e) => setFilterDriver(e.target.value)}
              placeholder="Driver Service No"
              className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-gray-400 focus:ring-1 focus:ring-gray-400 transition"
            />
          </div>
          
          <div className="relative">
            <Calendar size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-gray-400 focus:ring-1 focus:ring-gray-400 transition"
            />
          </div>
          
          <div className="relative">
            <Calendar size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-gray-400 focus:ring-1 focus:ring-gray-400 transition"
            />
          </div>
        </div>
        
        {(filterVehicle || filterDriver || fromDate || toDate) && (
          <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-100">
            <p className="text-xs text-gray-500">
              Showing {filteredBookings.length} filtered bookings
            </p>
            <button
              onClick={handleClearFilters}
              className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700 transition"
            >
              <X size={12} />
              Clear filters
            </button>
          </div>
        )}
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
        <ChartCard 
          title="Top Vehicles by Working Hours" 
          data={vehicleHours} 
          dataKey="vehicle_id" 
          color="blue"
          valueLabel="Hours worked per vehicle"
        />
        <ChartCard 
          title="Top Drivers by Working Hours" 
          data={driverHours} 
          dataKey="driver" 
          color="green"
          valueLabel="Hours worked per driver"
        />
        <ChartCard 
          title="Top Vehicles by Distance" 
          data={vehicleDistance} 
          dataKey="vehicle_id" 
          color="orange"
          valueLabel="Distance covered (km)"
        />
      </div>

      {/* Bookings Table */}
      <div className="bg-white border border-gray-100 rounded-lg shadow-sm overflow-hidden">
        <div className="p-4 border-b border-gray-100">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
            <div className="flex items-center gap-2">
              <FileText size={14} className="text-gray-400" />
              <h2 className="text-sm font-semibold text-gray-900">Booking Records</h2>
            </div>
            
            <div className="flex items-center gap-2">
              <select
                value={exportScope}
                onChange={(e) => setExportScope(e.target.value)}
                className="px-2 py-1 text-xs border border-gray-200 rounded-lg focus:outline-none focus:border-gray-400"
              >
                <option value="page">Current Page</option>
                <option value="all">All Filtered</option>
              </select>
              <button
                onClick={() => downloadPDF(exportScope)}
                className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition"
              >
                <Download size={12} />
                Export PDF
              </button>
            </div>
          </div>
        </div>
        
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-100">
              <tr>
                <th className="text-left py-2.5 px-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Booking ID
                </th>
                <th className="text-left py-2.5 px-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Vehicle ID
                </th>
                <th className="text-left py-2.5 px-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Driver
                </th>
                <th className="text-left py-2.5 px-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Start Time
                </th>
                <th className="text-left py-2.5 px-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                  End Time
                </th>
                <th className="text-left py-2.5 px-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Distance
                </th>
                <th className="text-left py-2.5 px-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {currentPageBookings.length > 0 ? (
                currentPageBookings.map((booking, idx) => (
                  <BookingRow key={booking.bId || idx} booking={booking} index={idx} />
                ))
              ) : (
                <tr>
                  <td colSpan={7} className="py-8 text-center">
                    <div className="flex flex-col items-center gap-1.5">
                      <FileText size={24} className="text-gray-300" />
                      <p className="text-xs text-gray-500">No bookings found</p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        
        {/* Pagination */}
        {filteredBookings.length > 0 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100">
            <p className="text-xs text-gray-500">
              Showing {(page - 1) * PAGE_SIZE + 1} to {Math.min(page * PAGE_SIZE, filteredBookings.length)} of {filteredBookings.length}
            </p>
            <div className="flex gap-1">
              <button
                onClick={() => setPage(Math.max(1, page - 1))}
                disabled={page === 1}
                className="px-2 py-1 text-xs font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded disabled:opacity-50 disabled:cursor-not-allowed transition"
              >
                Previous
              </button>
              <span className="px-2 py-1 text-xs text-gray-600">
                Page {page} of {pageCount}
              </span>
              <button
                onClick={() => setPage(Math.min(pageCount, page + 1))}
                disabled={page === pageCount}
                className="px-2 py-1 text-xs font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded disabled:opacity-50 disabled:cursor-not-allowed transition"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}